import React from 'react';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { isBefore, parseISO, addWeeks, addYears } from 'date-fns';

import { classListPropTypes, reactHookFormPropTypes } from 'constants/propTypes';
import { getOffsetDaysOfWeek } from 'helpers/intl-fns';
import Message from 'components/Message';
import When from 'components/When';
import Button from 'components/Button';
import { Card, CardTitle } from 'components/Card';
import { DropdownToggle, DropdownMenu, DropdownItem, DropdownItemCheckbox } from 'components/Dropdown2';
import {
  AutocompleteTimePicker,
  Button as FormButton,
  ButtonGroup,
  DateInput,
  Dropdown,
  Form,
  Input,
  RadioButton,
  RadioGroup,
} from 'components/HookForm';
import appMessages from 'features/App/App.messages';
import { messages } from './ClasstivitySchedulerForm.messages';

export const REPEAT_WEEKLY_OPTIONS = {
  YES: 'YES',
  NO: 'NO',
};

const MINIMUM_DURATION_IN_MINUTES = 5;
const MAXIMUM_DURATION_IN_MINUTES = 480;

export const DEFAULT_VALUES = {
  classId: null,
  days: [],
  repeatWeekly: REPEAT_WEEKLY_OPTIONS.NO,
  startAndDuration: [{ duration: null, startTime: null }],
  startDate: new Date().toISOString(),
  endDate: addWeeks(new Date(), 1).toISOString(),
};

const StartAndDurationArray = ({ name, control, className }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  return (
    <div className={className}>
      {fields.map((field, idx) => {
        const fieldName = `${name}.${idx}`;

        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={idx}>
            <AutocompleteTimePicker name={`${fieldName}.startTime`} label={messages.startTimeLabel} required />
            <div className="flex">
              <Input
                name={`${fieldName}.duration`}
                label={messages.durationLabel}
                type="number"
                required
                min={MINIMUM_DURATION_IN_MINUTES}
                max={MAXIMUM_DURATION_IN_MINUTES}
              />
            </div>
          </div>
        );
      })}

      <div className="flex">
        <Message
          message={messages.addStartTime}
          as={Button}
          type={Button.TYPES.BUTTON}
          styled="tertiary"
          onClick={() => append({})}
        />

        {fields.length > 1 && (
          <Button className="ml-" type={Button.TYPES.BUTTON} styled="danger" onClick={() => remove(0)}>
            <Message message={messages.removeStartTime} />
          </Button>
        )}
      </div>
    </div>
  );
};

StartAndDurationArray.propTypes = {
  name: PropTypes.string,
  control: reactHookFormPropTypes.control,
  className: PropTypes.string,
};

const ClasstivitySchedulerForm = ({
  startWeekOn,
  classes,
  onFormSubmit,
  onPublishClick,
  onResetClick,
  newSchedulesCount,
  openAddClassModal,
  newClassId,
  onResetNewClassId,
}) => {
  return (
    <Form className="grid grid--flush" defaultValues={DEFAULT_VALUES} onSubmit={onFormSubmit}>
      {({ control, watch, getValues, reset, setValue }) => {
        const isRepeatWeekly = watch('repeatWeekly') === REPEAT_WEEKLY_OPTIONS.YES;

        if (newClassId) {
          setValue('classId', newClassId);
        }

        const handleResetClick = () => {
          reset({
            classId: null,
            days: [],
            repeatWeekly: REPEAT_WEEKLY_OPTIONS.NO,
            startAndDuration: [{ duration: null, startTime: null }],
            startDate: new Date().toISOString(),
            endDate: addWeeks(new Date(), 1).toISOString(),
          });
          onResetClick();
        };

        const daysOfWeek = getOffsetDaysOfWeek(startWeekOn);

        const startEndDateValidator = {
          message: messages.startDateAfterEndDate,
          value: () => {
            const values = getValues();
            const repeatWeekly = _get(values, 'repeatWeekly');
            if (repeatWeekly === REPEAT_WEEKLY_OPTIONS.NO) return true;

            const start = _get(values, 'startDate');
            const end = _get(values, 'endDate');
            const parsedStartDate = parseISO(start);
            const parsedEndDate = parseISO(end);
            return isBefore(parsedStartDate, parsedEndDate);
          },
        };

        const recurrenceDaysValidator = {
          message: messages.recurrenceDaysRequired,
          value: () => {
            const values = getValues();
            const repeatWeekly = _get(values, 'repeatWeekly');
            if (repeatWeekly === REPEAT_WEEKLY_OPTIONS.NO) return true;

            const days = _get(values, 'days');
            return !_isEmpty(days);
          },
        };

        return (
          <Card>
            <Message as={CardTitle} message={messages.newSchedule} />
            <div className="grid__item 1/3 mt">
              <Dropdown name="classId" label={messages.classLabel} className="mb-l" required>
                {({ value, onChange }) => (
                  <>
                    <DropdownToggle>
                      {classes.find(({ classId }) => classId === value)?.className || (
                        <Message className="text--light" message={messages.selectClass} />
                      )}
                    </DropdownToggle>
                    <DropdownMenu>
                      {classes.map(({ classId, className }) => (
                        <DropdownItem
                          key={classId}
                          isSelected={classId === value}
                          onClick={() => {
                            onChange(classId);
                            onResetNewClassId();
                          }}>
                          {className}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </>
                )}
              </Dropdown>
              <Message
                className="mb-l"
                type={Button.TYPES.BUTTON}
                as={Button}
                styled="tertiary"
                message={messages.addClass}
                onClick={openAddClassModal}
              />
              <DateInput name="startDate" label={messages.startDateLabel} validate={startEndDateValidator} />
            </div>

            <StartAndDurationArray name="startAndDuration" control={control} className="grid__item 1/3" />

            <div className="grid__item 1/3">
              <RadioGroup name="repeatWeekly" label={messages.repeatWeeklyLabel}>
                <RadioButton alignLeft key={REPEAT_WEEKLY_OPTIONS.YES} value={REPEAT_WEEKLY_OPTIONS.YES}>
                  <Message message={appMessages.yes} />
                </RadioButton>
                <RadioButton alignLeft key={REPEAT_WEEKLY_OPTIONS.NO} value={REPEAT_WEEKLY_OPTIONS.NO}>
                  <Message message={appMessages.no} />
                </RadioButton>
              </RadioGroup>

              <When predicate={isRepeatWeekly}>
                <Dropdown
                  className="mt-"
                  name="days"
                  closeOnSelect={false}
                  multiselect
                  validate={recurrenceDaysValidator}>
                  {({ value, onChange }) => {
                    const dayOptions = daysOfWeek.map(day => ({ id: day.value, dateTime: day.dateTime }));
                    const isAllDaysSelected = value?.length === dayOptions.length;

                    return (
                      <>
                        <DropdownToggle label={messages.daysLabel}>
                          <Message
                            {...(isAllDaysSelected ? messages.allDaysSelected : messages.xDaysSelected)}
                            values={{ daysCount: value.length }}
                          />
                        </DropdownToggle>
                        <DropdownMenu>
                          <DropdownItemCheckbox
                            isSelectAll
                            isChecked={isAllDaysSelected}
                            onClick={() => onChange(isAllDaysSelected ? [] : dayOptions.map(({ id }) => id))}
                          />
                          {dayOptions.map(({ id, dateTime }) => (
                            <DropdownItemCheckbox
                              key={id}
                              isChecked={value.includes(id)}
                              onClick={checked => {
                                const updatedArr = checked ? value.concat(id) : value.filter(opt => opt !== id);
                                onChange(updatedArr);
                              }}>
                              <Message message={messages.dow} values={{ dateTime }} />
                            </DropdownItemCheckbox>
                          ))}
                        </DropdownMenu>
                      </>
                    );
                  }}
                </Dropdown>

                <DateInput
                  name="endDate"
                  label={messages.endDateLabel}
                  className="mt"
                  validate={startEndDateValidator}
                  modifiers={{ disabled: { before: new Date(), after: addYears(new Date(), 1) } }}
                />
              </When>
            </div>

            <ButtonGroup>
              <Message as={FormButton} type={Button.TYPES.SUBMIT} styled="tertiary" message={messages.applyButton} />
              <Message
                onClick={onPublishClick}
                disabled={newSchedulesCount === 0}
                as={FormButton}
                type={Button.TYPES.BUTTON}
                message={messages.publish}
              />
              <Message
                onClick={handleResetClick}
                disabled={newSchedulesCount === 0}
                as={FormButton}
                type={Button.TYPES.RESET}
                styled="danger"
                message={messages.reset}
              />
            </ButtonGroup>
          </Card>
        );
      }}
    </Form>
  );
};

ClasstivitySchedulerForm.propTypes = {
  startWeekOn: PropTypes.string,
  classes: classListPropTypes,
  onFormSubmit: PropTypes.func,
  onPublishClick: PropTypes.func,
  onResetClick: PropTypes.func,
  newSchedulesCount: PropTypes.number,
  openAddClassModal: PropTypes.func,
  newClassId: PropTypes.string,
  onResetNewClassId: PropTypes.func,
};

export default ClasstivitySchedulerForm;
